// @flow
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { closePanel } from 'esm-web-pro/redux/side-panel';
import { MapContext } from 'esm-web-pro/containers/map/context';
import { FormattedMessage } from 'react-intl';
import { History, Match } from 'react-router';
import {
    cloneDeep, get, isEmpty, set, unset, isEqual, isPlainObject,
} from 'lodash';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import connectAPI from 'speed-js-react/src/api/connectAPI';
import Spinner from 'esm-web-pro/components/forms/Spinner';
import FilterObjectsSidePanelFields from './FilterObjectsSidePanelFields';
import FilterObjectsSidePanelContainer from './FilterObjectsSidePanelContainer';

export const FILTER_OBJECTS_PANEL_KEY = 'FILTER_OBJECTS_PANEL_KEY';

// Initial resources (common for all lines)
const getLineL11Resources = (mapIdentity) => ([
    {
        key: 'provider',
        resourceName: 'infrastructureProvider',
        resourceArguments: [{ map_identity: mapIdentity }],
        labelFields: ['entity.name'],
        valueField: 'id',
        label: <FormattedMessage
            id="filter-objects-infrastructure-provider-lines"
            defaultMessage="Infrastructure Provider"
        />,
        multi: true,
    },
    {
        key: 'line11_objects',
        choices: {
            pipe: <FormattedMessage id="networks-cable-pipe" defaultMessage="Pipe" />,
            air_route: <FormattedMessage id="airroute-content-title" defaultMessage="Air Route" />,
        },
        label: <FormattedMessage id="filter-objects-line-type" defaultMessage="Line type" />,
        multi: true,
    },
]);

// Initial resources (common for all points)
const getPointLayer11Resources = (mapIdentity) => ([
    {
        key: 'provider',
        resourceName: 'infrastructureProvider',
        resourceArguments: [{ map_identity: mapIdentity }],
        labelFields: ['entity.name'],
        valueField: 'id',
        label: <FormattedMessage
            id="filter-objects-infrastructure-provider"
            defaultMessage="Infrastructure Provider"
        />,
        multi: true,
    },
    {
        key: 'export',
        choices: {
            true: <FormattedMessage id="filter-objects-export-yes" defaultMessage="Yes" />,
            false: <FormattedMessage id="filter-objects-export-no" defaultMessage="No" />,
        },
        label: <FormattedMessage id="filter-objects-export" defaultMessage="Export" />,
    },
    {
        key: 'point11_objects',
        choices: {
            shaft: <FormattedMessage id="networks-map-points-shaft" defaultMessage="Shaft" />,
            wallpoint: <FormattedMessage id="networks-map-points-wallpoint" defaultMessage="Wall point" />,
            pop: <FormattedMessage id="networks-map-points-pop" defaultMessage="PoP" />,
            pillar: <FormattedMessage id="networks-map-points-pillar" defaultMessage="Pillar" />,
            fork: <FormattedMessage id="networks-map-points-fork" defaultMessage="Fork" />,
            linecut: <FormattedMessage id="networks-map-points-linecut" defaultMessage="Line Cut" />,
            cabinet: <FormattedMessage id="networks-map-points-cabinet" defaultMessage="Cabinet" />,

        },
        label: <FormattedMessage id="filter-objects-object-type" defaultMessage="Point type" />,
        multi: true,
    },
]);

const titles = {
    gisPoint: <FormattedMessage id="gis-point-title" defaultMessage="Gis Points" />,
    gisLine: <FormattedMessage id="gis-line-title" defaultMessage="Gis Lines" />,
    shaft: <FormattedMessage id="networks-map-points-shaft" defaultMessage="Shaft" />,
    wallpoint: <FormattedMessage id="networks-map-points-wallpoint" defaultMessage="Wall point" />,
    pop: <FormattedMessage id="networks-map-points-pop" defaultMessage="PoP" />,
    pillar: <FormattedMessage id="networks-map-points-pillar" defaultMessage="Pillar" />,
    fork: <FormattedMessage id="networks-map-points-fork" defaultMessage="Fork" />,
    linecut: <FormattedMessage id="networks-map-points-linecut" defaultMessage="Line Cut" />,
    cabinet: <FormattedMessage id="networks-map-points-cabinet" defaultMessage="Cabinet" />,
    pipe: <FormattedMessage id="networks-cable-pipe" defaultMessage="Pipe" />,
    air_route: <FormattedMessage id="airroute-content-title" defaultMessage="Air Route" />,
};

export
type Props = {
    getData: Function,
    getDataNested: Function,
    editor: Object,
    history: History,
    match: Match,
};
const FilterObjectsContainer = (props: Props) => {
    const {
        history, match, editor, getData, getDataNested,
    } = props;

    const map = useContext(MapContext);

    // resources
    const pointLayer11Models = {
        shaft: {
            key: 'shaft_model',
            resourceName: 'nomenclatureItemNested',
            resourceArguments: [115],
            labelFields: ['translations.bg.name'],
            valueField: 'code',
            label: <FormattedMessage id="filter-objects-shaft-model" defaultMessage="Shaft model" />,
            multi: true,
        },
        pillar: {
            key: 'pillar_model',
            resourceName: 'nomenclatureItemNested',
            resourceArguments: [116],
            labelFields: ['translations.bg.name'],
            valueField: 'code',
            label: <FormattedMessage id="filter-objects-pillar-model" defaultMessage="Pillar model" />,
            multi: true,
        },
        pop: {
            key: 'pop_model',
            resourceName: 'nomenclatureItemNested',
            resourceArguments: [130],
            labelFields: ['translations.bg.name'],
            valueField: 'code',
            label: <FormattedMessage id="filter-objects-pop-model" defaultMessage="Pop model" />,
            multi: true,
        },
        provider: {
            key: 'provider',
            resourceName: 'infrastructureProvider',
            resourceArguments: [{ map_identity: map?.identity }],
            labelFields: ['entity.name'],
            valueField: 'id',
            label: <FormattedMessage
                id="filter-objects-infrastructure-provider"
                defaultMessage="Infrastructure Provider"
            />,
            multi: true,
        },
        export: {
            key: 'export',
            choices: {
                True: <FormattedMessage id="filter-objects-export-yes" defaultMessage="Yes" />,
                False: <FormattedMessage id="filter-objects-export-no" defaultMessage="No" />,
            },
            label: <FormattedMessage id="filter-objects-export" defaultMessage="Export" />,
        },
    };

    // pointLayer 1_2 model resources
    const pointLayer12Models = {
        terminalbox: {
            key: 'terminalbox_model',
            resourceName: 'nomenclatureItemNested',
            resourceArguments: [118],
            labelFields: ['translations.bg.name'],
            valueField: 'code',
            label: <FormattedMessage id="filter-objects-terminalbox-model" defaultMessage="Terminal box model" />,
        },
        switch: {
            key: 'switch_model',
            resourceName: 'nomenclatureItemNested',
            resourceArguments: [123],
            labelFields: ['translations.bg.name'],
            valueField: 'code',
            label: <FormattedMessage id="filter-objects-switch-model" defaultMessage="Switch model" />,
        },
        coupler: {
            key: 'coupler_model',
            resourceName: 'nomenclatureItemNested',
            resourceArguments: [119],
            labelFields: ['translations.bg.name'],
            valueField: 'code',
            label: <FormattedMessage id="filter-objects-coupler-model" defaultMessage="Coupler model" />,
            multi: true,
        },
        odf: {
            key: 'odf_model',
            resourceName: 'nomenclatureItemNested',
            resourceArguments: [117],
            labelFields: ['translations.bg.name'],
            valueField: 'code',
            label: <FormattedMessage id="filter-objects-odf-model" defaultMessage="ODF model" />,
        },
        pipe: {
            key: 'pipe_model',
            resourceName: 'nomenclatureItemNested',
            resourceArguments: [120],
            labelFields: ['translations.bg.name'],
            valueField: 'code',
            label: <FormattedMessage id="filter-objects-pipe-model" defaultMessage="Pipe model" />,
            multi: true,
        },
    };

    // This function is to be called after the fetch for the primary resources is done
    const getSecondaryResources = () => ({
        shaft: [
            { ...pointLayer11Models.shaft },
            { ...pointLayer12Models.coupler },
            { ...pointLayer11Models.provider },
            { ...pointLayer11Models.export },
        ],
        wallpoint: [
            { ...pointLayer12Models.coupler },
            { ...pointLayer12Models.terminalbox },
            { ...pointLayer11Models.provider },
            { ...pointLayer11Models.export },
        ],
        pop: [
            { ...pointLayer11Models.pop },
            { ...pointLayer12Models.odf },
            { ...pointLayer12Models.switch },
            { ...pointLayer11Models.provider },
            { ...pointLayer11Models.export },
        ],
        pillar: [
            { ...pointLayer11Models.pillar },
            { ...pointLayer12Models.coupler },
            { ...pointLayer12Models.terminalbox },
            { ...pointLayer11Models.provider },
            { ...pointLayer11Models.export },
        ],
        fork: [
            { ...pointLayer11Models.provider },
            { ...pointLayer11Models.export },
        ],
        linecut: [
            { ...pointLayer11Models.provider },
            { ...pointLayer11Models.export },
        ],
        cabinet: [
            { ...pointLayer11Models.provider },
            { ...pointLayer11Models.export },
        ],
        pipe: [
            { ...pointLayer12Models.pipe },
            { ...pointLayer11Models.provider },
            { ...pointLayer11Models.export },
        ],
        air_route: [
            { ...pointLayer11Models.provider },
        ],
    });

    // State for GISPoints
    const pointLayer11Resources = React.useMemo(() => (
        getPointLayer11Resources(map?.identity)
    ), [map?.identity]);
    const [resources, setResources] = useState(() => (
        getPointLayer11Resources(map?.identity)
    ));
    const [pointFilterValues, setPointFilterValues] = useState({});
    const [additionalResources, setAdditionalResources] = useState({});
    const [additionalFiltersValues, setAdditionalFiltersValues] = useState({});
    const [additionalFiltersValuesQueryString, setAdditionalFiltersValuesQueryString] = useState([]);

    // State for GISLines
    const lineL11Resources = React.useMemo(() => (
        getLineL11Resources(map?.identity)
    ), [map?.identity]);
    const [resourcesLine, setResourcesLine] = useState(() => (
        getLineL11Resources(map?.identity)
    ));
    const [lineFilterValues, setLineFilterValues] = useState({});
    const [additionalResourcesLine, setAdditionalResourcesLine] = useState([]);
    const [additionalFiltersValuesLine, setAdditionalFiltersValuesLine] = useState({});
    const [additionalFiltersValuesQueryStringLine, setAdditionalFiltersValuesQueryStringLine] = useState([]);

    const [secondaryResources, setSecondaryResources] = useState({});

    const [loading, setLoading] = useState(true);
    const [filtersState, setFiltersState] = useState({});

    const dispatch = useDispatch();

    // Fetch data for resource and set it to the resource choice field
    // eslint-disable-next-line consistent-return
    const fetchData = (resource) => {
        const {
            resourceName, resourceArguments, labelFields, valueField, key,
        } = resource;
        let choices = {};
        // TODO: Generalize
        if (key !== 'ref' && key !== 'provider_ref' && resourceName) {
            if (resourceArguments && resourceArguments.length > 0) {
                return getDataNested(resourceName, resourceArguments[0])
                    .then((res) => {
                        const { results } = res;
                        results.forEach((result) => {
                            choices = {
                                ...choices,
                                [result[valueField]]: get(result, labelFields[0]),
                            };
                        });
                        resource.choices = choices;
                    })
                    .catch((err) => console.log('Err....', err));
            }
            return getData(resourceName)
                .then((res) => {
                    const { results } = res;
                    results.forEach((result) => {
                        choices = {
                            ...choices,
                            [result[valueField]]: get(result, labelFields[0]),
                        };
                    });
                    resource.choices = choices;
                })
                .catch((err) => console.log('Err....', err));
        }
    };

    // Check the resource argument type and push the request to an array
    const setChoices = React.useCallback((resource) => {
        const promiseArr = [];
        if (Array.isArray(resource)) {
            resource.forEach((res) => {
                promiseArr.push(fetchData(res));
            });
        } else {
            Object.keys(resource).forEach((key) => {
                promiseArr.push(fetchData(resource[key]));
            });
        }
        return promiseArr;
    }, []);

    // Check if nested object is empty(has no values)
    const isEmptyState = (state) => {
        let empty = true;
        Object.keys(state).forEach((key) => {
            if (key !== 'resources' && key !== 'resourcesLine') {
                if (!isEmpty(state[key])) {
                    empty = false;
                }
            }
        });
        return empty;
    };

    const getDataFromSessionStorage = () => {
        if (window.sessionStorage.getItem('mapFilters')) {
            const mapFilters = JSON.parse(window.sessionStorage.getItem('mapFilters'));
            if (!isEmptyState(mapFilters)) {
                setPointFilterValues(mapFilters.pointFilterValues);
                setAdditionalFiltersValues(mapFilters.additionalFiltersValues);
                setAdditionalFiltersValuesQueryString(mapFilters.additionalFiltersValuesQueryString);

                setLineFilterValues(mapFilters.lineFilterValues);
                setAdditionalFiltersValuesLine(mapFilters.additionalFiltersValuesLine);
                setAdditionalFiltersValuesQueryStringLine(mapFilters.additionalFiltersValuesQueryStringLine);
            }
        }
    };

    // Loop through all resources and execute all requests asynchronously
    useEffect(() => {
        const resourcesArray = [lineL11Resources, pointLayer11Resources, pointLayer11Models, pointLayer12Models];
        const promiseArray = [];
        resourcesArray.forEach((res) => promiseArray.push(...setChoices(res)));
        Promise.all(promiseArray)
            .then(() => {
                setResources([...pointLayer11Resources]);
                setResourcesLine([...lineL11Resources]);
                setSecondaryResources(getSecondaryResources());
                getDataFromSessionStorage();
                setLoading(false);
            })
            .catch((err) => console.log(err));
    }, []);

    // Check how many filters are applied
    const getFiltersCount = (arr) => {
        let count = 0;
        const test = (state) => {
            Object.keys(state).forEach((item) => {
                if (state[item] !== null && state[item] !== {} && isPlainObject(state[item]) && state[item] !== []) {
                    test(state[item]);
                } else if (!isEmpty(state[item]) && state[item] !== null && state[item] !== {} && state[item] !== []) {
                    count += 1;
                }
            });
        };
        arr.forEach((state) => {
            test(state);
        });
        return count;
    };

    // Set filters count to session storage
    const setFiltersCountToSessionStorage = () => {
        const values = [pointFilterValues, additionalFiltersValues, lineFilterValues, additionalFiltersValuesLine];
        window.sessionStorage.setItem('mapFiltersCount', JSON.stringify(getFiltersCount(values)));
    };

    // Set session storage if state is not Empty
    const setFiltersToSessionStorage = () => {
        if (!isEmptyState(filtersState)) {
            if (window.sessionStorage.getItem('mapFilters')) {
                const sessionStorageState = JSON.parse(window.sessionStorage.getItem('mapFilters'));
                if (!isEqual(sessionStorageState, filtersState)) {
                    const mapFilters = JSON.stringify(filtersState);
                    window.sessionStorage.setItem('mapFilters', mapFilters);
                }
            } else {
                const mapFilters = JSON.stringify(filtersState);
                window.sessionStorage.setItem('mapFilters', mapFilters);
            }
        }
    };

    // Make General state object to save in session storage
    useEffect(() => {
        setFiltersState({
            pointFilterValues,
            additionalFiltersValues,
            additionalFiltersValuesQueryString,
            lineFilterValues,
            additionalFiltersValuesLine,
            additionalFiltersValuesQueryStringLine,
        });
    }, [pointFilterValues, additionalFiltersValues, additionalFiltersValuesQueryString, lineFilterValues,
        additionalFiltersValuesLine, additionalFiltersValuesQueryStringLine]);

    // Toggle disable primary filters for points when additional filters are enabled
    useEffect(() => {
        const toggleResources = (bool) => {
            // Disable most of the common filter fields when additional filters are enabled
            const updatedResources = resources.map((resource) => {
                if (resource.key !== 'point11_objects') {
                    return {
                        ...resource,
                        disabled: bool,
                    };
                }
                return resource;
            });
            if (updatedResources) {
                setResources(updatedResources);
            }
        };
        if (!isEmpty(additionalResources)) {
            const { provider, ref } = pointFilterValues;
            // Reset ref value if any additional filters are applied
            if (ref) {
                setPointFilterValues({
                    ...pointFilterValues,
                    ref: '',
                });
            }
            // If additional filter is chosen transfers the provider value to the additional filters
            if (provider) {
                pointFilterValues.point11_objects.forEach((val) => {
                    if (!additionalFiltersValues[val]) {
                        setAdditionalFiltersValues({
                            ...additionalFiltersValues,
                            [val]: {
                                provider,
                            },
                        });
                    }
                });
            }
            toggleResources(true);
        } else {
            toggleResources(false);
        }
    }, [additionalResources]);

    // Toggle disable primary filters for lines when additional filters are enabled
    useEffect(() => {
        const toggleResources = (bool) => {
            // Disable most of the common filter fields when additional filters are enabled
            const updatedResources = resourcesLine.map((resource) => {
                if (resource.key !== 'line11_objects') {
                    return {
                        ...resource,
                        disabled: bool,
                    };
                }
                return resource;
            });
            if (updatedResources) {
                setResourcesLine(updatedResources);
            }
        };
        if (!isEmpty(additionalResourcesLine)) {
            const { provider, ref } = lineFilterValues;
            // Reset ref value if any additional filters are applied
            if (ref) {
                setLineFilterValues({
                    ...lineFilterValues,
                    ref: '',
                });
            }
            // If additional filter is chosen transfers the provider value to the additional filters
            if (provider) {
                lineFilterValues.line11_objects.forEach((val) => {
                    if (!additionalFiltersValuesLine[val]) {
                        setAdditionalFiltersValuesLine({
                            ...additionalFiltersValuesLine,
                            [val]: {
                                provider,
                            },
                        });
                    }
                });
            }
            toggleResources(true);
        } else {
            toggleResources(false);
        }
    }, [additionalResourcesLine]);

    const setQueryString = (values, cb) => {
        if (!isEmpty(values)) {
            const queryObject = {};
            Object.entries(values).forEach(([key, val]) => {
                Object.entries(val).forEach(([key1, val1]) => {
                    if (Array.isArray(val1)) {
                        const arr = [];
                        const queryStringKey = `${key}_${key1}`;
                        val1.forEach((v) => {
                            arr.push(`${v}`);
                        });
                        queryObject[queryStringKey] = [...arr];
                    } else {
                        const queryStringKey = `${key}_${key1}`;
                        queryObject[queryStringKey] = `${val1}`;
                    }
                });
            });
            cb(queryObject);
        } else {
            cb([]);
        }
    };

    // Create custom queryStrings out of nested objects in additionalFiltersValues
    useEffect(() => {
        setQueryString(additionalFiltersValues, setAdditionalFiltersValuesQueryString);
    }, [additionalFiltersValues]);

    useEffect(() => {
        setQueryString(additionalFiltersValuesLine, setAdditionalFiltersValuesQueryStringLine);
    }, [additionalFiltersValuesLine]);

    // Filtering Enabled/Disabled
    useEffect(() => {
        map.is_filtering = true;
    }, []);

    // Create a filter objects which are later used for queryStrings
    const saveFilterValuesToMapContext = () => {
        map.filters = {};
        map.filters.points = { ...pointFilterValues, ...additionalFiltersValuesQueryString };
        map.filters.lines = { ...lineFilterValues, ...additionalFiltersValuesQueryStringLine };

        window.sessionStorage.setItem('pointFilters',
            JSON.stringify({ ...pointFilterValues, ...additionalFiltersValuesQueryString }));

        window.sessionStorage.setItem('lineFilters',
            JSON.stringify({ ...lineFilterValues, ...additionalFiltersValuesQueryStringLine }));
    };

    const renderAdditionalFields = (resource, cb) => {
        const layer11Objects = resource === 'point'
            ? pointFilterValues.point11_objects : lineFilterValues.line11_objects;
        if (layer11Objects && layer11Objects.length > 0) {
            let tempResourcesObject = {};
            layer11Objects.forEach((item) => {
                if (secondaryResources[item]) {
                    tempResourcesObject = {
                        ...tempResourcesObject,
                        [item]: secondaryResources[item],
                    };
                }
            });
            cb({ ...tempResourcesObject });
        } else {
            cb([]);
        }
    };

    // Renders point layer 1.2 filter fields for different L1.1 objects
    useEffect(() => {
        renderAdditionalFields('point', setAdditionalResources);
    }, [pointFilterValues]);

    useEffect(() => {
        renderAdditionalFields('line', setAdditionalResourcesLine);
    }, [lineFilterValues]);

    // The Callback is a setState function
    const handleChange = (value, key, prevValues, cb) => {
        cb({
            ...prevValues,
            [key]: value,
        });
    };

    const handleChangeSecondaryFilters = (value, resourceName, objectType, cb) => {
        cb((prevAdditionalFiltersValues) => {
            let newAdditionalFiltersValues = cloneDeep(prevAdditionalFiltersValues);
            const propertyPath = `${objectType}.${resourceName}`;
            if (value) {
                newAdditionalFiltersValues = set(newAdditionalFiltersValues, propertyPath, value);
            } else {
                unset(newAdditionalFiltersValues, propertyPath);
            }
            if (isEmpty(get(newAdditionalFiltersValues, objectType, {}))) {
                // Empty key, remove it
                unset(newAdditionalFiltersValues, objectType);
            }
            return newAdditionalFiltersValues;
        });
    };

    const deleteAdditionalFilters = (resource, values, cb) => {
        const objectTypes = resource === 'point' ? pointFilterValues.point11_objects : lineFilterValues.line11_objects;
        if (objectTypes && !isEmpty(objectTypes)) {
            Object.keys(values).forEach((val) => {
                if (!objectTypes.includes(val)) {
                    const object = { ...values };
                    delete object[val];
                    cb({ ...object });
                }
            });
        } else {
            cb({});
        }
    };

    // Delete additional filters if an object type is removed from the multiSelect
    useEffect(() => {
        deleteAdditionalFilters('point', additionalFiltersValues, setAdditionalFiltersValues);
    }, [pointFilterValues]);

    useEffect(() => {
        deleteAdditionalFilters('line', additionalFiltersValuesLine, setAdditionalFiltersValuesLine);
    }, [lineFilterValues]);

    // Reset all the state and session storage
    const resetFilters = () => {
        if (window.sessionStorage.getItem('mapFilters')) {
            window.sessionStorage.removeItem('mapFilters');
        }
        if (window.sessionStorage.getItem('mapFiltersCount')) {
            window.sessionStorage.removeItem('mapFiltersCount');
        }
        if (window.sessionStorage.getItem('pointFilters')) {
            window.sessionStorage.removeItem('pointFilters');
        }
        if (window.sessionStorage.getItem('lineFilters')) {
            window.sessionStorage.removeItem('lineFilters');
        }

        setPointFilterValues({});
        setAdditionalResources({});
        setAdditionalFiltersValues({});
        setAdditionalFiltersValuesQueryString([]);

        setResourcesLine([...lineL11Resources]);
        setLineFilterValues({});
        setAdditionalResourcesLine([]);
        setAdditionalFiltersValuesLine({});
        setAdditionalFiltersValuesQueryStringLine([]);

        setFiltersState({});
    };

    const handleClose = (resetState: boolean) => {
        if (resetState) {
            resetFilters();
            editor._loadFeatures();
        }
        dispatch(closePanel(FILTER_OBJECTS_PANEL_KEY, true));
        history.push(match.url.split('/filter')[0]);
        editor._loadFeatures();
    };

    const handleSubmit = () => {
        setFiltersToSessionStorage();
        setFiltersCountToSessionStorage();
        saveFilterValuesToMapContext();
        handleClose(false);
    };

    return (
        <FilterObjectsSidePanelContainer
            onClose={() => handleClose(true)}
            onSubmit={handleSubmit}
        >
            { loading
                ? (
                    <Spinner
                        type="clipLoader"
                        loading={loading}
                        blocking={false}
                        color="black"
                        coverStyle={{
                            position: 'absolute',
                            margin: 'auto',
                            width: '100%',
                            height: '100%',
                        }}
                    />
                )
                : (
                    <>
                        <FilterObjectsSidePanelFields
                            title={titles.gisLine}
                            titles={titles}
                            resources={resourcesLine}
                            additionalResources={additionalResourcesLine}
                            values={lineFilterValues}
                            secondaryFilterValues={additionalFiltersValuesLine}
                            onChange={handleChange}
                            onChangeSecondary={handleChangeSecondaryFilters}
                            setFilterValues={setLineFilterValues}
                            setAdditionalFiltersValues={setAdditionalFiltersValuesLine}
                        />
                        <FilterObjectsSidePanelFields
                            title={titles.gisPoint}
                            titles={titles}
                            resources={resources}
                            additionalResources={additionalResources}
                            values={pointFilterValues}
                            secondaryFilterValues={additionalFiltersValues}
                            onChange={handleChange}
                            onChangeSecondary={handleChangeSecondaryFilters}
                            setFilterValues={setPointFilterValues}
                            setAdditionalFiltersValues={setAdditionalFiltersValues}
                        />
                    </>
                )
            }
        </FilterObjectsSidePanelContainer>
    );
};

const apiCallback = (apiClient: ApiClient) => ({
    getData: {
        send: (resourceName) => apiClient.resources[resourceName].list(),
        success: (res) => res.data,
        error: (err) => console.log(err),
    },
    getDataNested: {
        send: (resourceName, parentId) => apiClient.resources[resourceName].list(parentId),
        success: (res) => res.data,
        error: (err) => console.log(err),
    },
});

export default connectAPI(apiCallback)(FilterObjectsContainer);
